import { CommonModule } from '@angular/common';
import { Component, inject, Injectable } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDialogService, NonNullableConfigData, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { lastValueFrom } from 'rxjs';
import { ConfigurationManagerService, UiLanguage, uiLanguages } from 'src/app/core';
import { TrimInputDirective } from 'src/app/directives/trim.directive';
import { AlternativeTranslate } from 'tilby-models';

export interface TranslateFieldDialogData {
  mainValue: string;
  alternative_names: AlternativeTranslate[];
  nameMaxLength: number;
}

export interface TranslationGroup {
  iso_code: CustomFormControl<string | null>;
  title: CustomFormControl<string | null>;
  valueField: CustomFormControl<string | null>;
  disabled: CustomFormControl<boolean | null>;
}

export interface TranslationForm {
  translations: CustomFormArray<CustomFormGroup<TranslationGroup>>;
  [key: string]: any;
}

export interface TranslationValue {
  iso_code: string;
  title: string;
  valueField: string;
  disabled: boolean;
}

@Component({
  selector: 'app-translate-field-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
    TranslateModule,
    TrimInputDirective
  ],
  templateUrl: './translate-field-dialog.component.html',
  styleUrl: './translate-field-dialog.component.scss'
})
export class TranslateFieldDialogComponent {

  protected readonly data: TranslateFieldDialogData = inject(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly configurationManager = inject(ConfigurationManagerService);

  protected form!: CustomFormGroup<TranslationForm>;

  constructor() {
    this.buildForm();
  }

  private buildForm() {
    this.form = new CustomFormGroup<TranslationForm>({
      'translations': new CustomFormArray<CustomFormGroup<TranslationGroup>>([])
    });
    this.buildTranslationsList();
  }

  get translations(): CustomFormArray<CustomFormGroup<TranslationGroup>> {
    return this.form.get('translations') as CustomFormArray<CustomFormGroup<TranslationGroup>>;
  }

  private buildTranslationsList() {
    const generalUiLanguage = this.configurationManager.getShopPreference('general.ui_language');

    Object.values(uiLanguages).forEach((uiLang: UiLanguage, index: number) => {
      const isDisabled = uiLang.id === generalUiLanguage;
      let initialValueField = '';

      if(!isDisabled) {
        initialValueField = this.data.alternative_names?.find((alt: AlternativeTranslate) => alt.iso_language === uiLang.id)?.value || '';
      }

      this.translations.push(
        new CustomFormGroup<TranslationGroup>({
          iso_code: new CustomFormControl(uiLang.id),
          title: new CustomFormControl(uiLang.value),
          valueField: new CustomFormControl(
            isDisabled? this.data.mainValue: initialValueField, 
            { validators: [Validators.maxLength(this.data.nameMaxLength)] }
          ),
          disabled: new CustomFormControl(isDisabled)
        })
      )

      if(isDisabled) {
        this.translations.at(index).get('valueField')?.disable();
      }
    })
  }

  onSave(): void {
    this.matDialogRef?.close(this.translations.getRawValue());
  }
}

@Injectable({
    providedIn: "root",
})
export class TranslateFieldDialogService extends BaseDialogService {
  private readonly dialogRef = inject(MatDialog);

  public openDialog(config?: NonNullableConfigData<TranslateFieldDialogData>): Promise<TranslationValue[]> {
    const data: TranslateFieldDialogData = config?.data || {mainValue: '', alternative_names: [], nameMaxLength: 255};
    const configEdited: NonNullableConfigData<TranslateFieldDialogData> = {
      ...config,
      ...this.switchMobileDesktopDimensions({width: '600px'}, {fullScreenForMobile: true}),
      disableClose: true,
      data,
    };
    return lastValueFrom(
      this.dialogRef.open(TranslateFieldDialogComponent, configEdited).afterClosed()
    );
  }
}
